import { viewFetch as fetch } from '../view-as.js';
// The line and the rundown as the server shapes them (apps/api/src/copilot/rundown.ts; pipeline RC5, module 10 U352 to
// U358). The portal cannot import the API's files, so the shapes are written again here; every word in them is the
// server's, and the portal only lays them out (U352: the record, the brief and the line say the same thing).

export interface Plate { text: string; red: boolean }
export interface LinePart { bold: string | null; text: string; plate: Plate | null; none: boolean }

export interface CompanyLineView {
  id: string;
  name: string;
  brands: Array<{ id: string; name: string }>;
  parent: { id: string; name: string } | null;
  kind: string | null;
  handledBy: LinePart;
  agreements: LinePart;
  billed: LinePart;
  deals: LinePart;
  /** The company's stored logo (pipeline RC7), or null: the initials are drawn instead. Optional until every server sends it. */
  logo?: string | null;
}

export interface Lede { lead: string; rest: string | null }

export type RundownRow =
  | { kind: 'dated'; when: string; label: string | null; text: string; link: null | { to: string; id: string; label: string } }
  | { kind: 'person'; name: string; company: { id: string; name: string } | null; status: string | null; text: string }
  | { kind: 'owed'; side: 'ours' | 'theirs'; who: string; text: string; quote: string | null; meta: string | null; notOnRecord: boolean };

export interface StakeCell {
  key: 'billed' | 'forecast' | 'agreements' | 'deals';
  label: string;
  figure: string;
  word: boolean;
  split: Array<{ label: string; value: string }>;
  deals: Array<{ id: string; title: string; figure: string | null; stage: string; stageCode: string | null }>;
  plate: Plate | null;
  plateNote: string | null;
  note: string | null;
}

export type SectionKey = 'stake' | 'meeting' | 'people' | 'owed' | 'operations' | 'moved';

export interface RundownSection {
  key: SectionKey;
  title: string;
  sub: string | null;
  lede: Lede;
  cells: StakeCell[];
  rows: RundownRow[];
  more: string | null;
  use: Lede | null;
}

export interface RundownData {
  line: CompanyLineView;
  meeting: { title: string | null; startsAt: string; endsAt: string; when: string; where: string | null } | null;
  sections: RundownSection[];
  notSeen: string[];
}

export interface RundownView { rundown: RundownData | null; why: string | null }

/** The reader's own zone, so "today" and "tomorrow" are theirs (module 13 DESIGN Open 23). */
export function readerZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** What the rundown is anchored on: a meeting on the person's calendar, a recorded meeting, or a company. */
export type RundownAnchor = { event: string; org?: string } | { meeting: string } | { org: string; event?: string };

export function rundownPath(store: string, anchor: RundownAnchor, zone = readerZone()): string {
  const q = new URLSearchParams({ store });
  for (const [k, v] of Object.entries(anchor)) if (typeof v === 'string' && v) q.set(k, v);
  q.set('zone', zone);
  return `/api/rundown?${q.toString()}`;
}

export async function fetchRundown(store: string, anchor: RundownAnchor): Promise<RundownView> {
  const r = await fetch(rundownPath(store, anchor), { credentials: 'same-origin' });
  if (!r.ok) throw new Error(r.status === 401 ? 'You are signed out.' : `Cobrain could not read the rundown (${r.status}).`);
  return (await r.json()) as RundownView;
}
